<template>
    <CalendarRoot
        v-slot="{ weekDays, grid, date, locale }"
        v-bind="{ ...$attrs, ...rootProps }"
        class="calendar"
        :class="[surfaceClass, { 'has-long-weekdays': weekdayFormat === 'long' }]">
        <CalendarHeader class="head">
            <CalendarHeading v-slot="{ headingValue }" class="heading">
                <template v-if="grid.length === 1">
                    <template v-for="(part, index) in headingParts(date, locale)" :key="index">
                        <span v-if="part.type === 'year'" class="year">{{ part.value }}</span>
                        <template v-else>{{ part.value }}</template>
                    </template>
                </template>
                <template v-else>{{ headingValue }}</template>
            </CalendarHeading>
            <div class="nav">
                <CalendarPrev class="nav-button">
                    <svg
                        class="nav-chevron"
                        viewBox="0 0 16 16"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="1.6"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        aria-hidden="true">
                        <path d="M10 3L5 8l5 5" />
                    </svg>
                </CalendarPrev>
                <CalendarNext class="nav-button">
                    <svg
                        class="nav-chevron"
                        viewBox="0 0 16 16"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="1.6"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        aria-hidden="true">
                        <path d="M6 3l5 5-5 5" />
                    </svg>
                </CalendarNext>
            </div>
        </CalendarHeader>
        <div class="months">
            <CalendarGrid v-for="month in grid" :key="month.value.toString()" class="month">
                <CalendarGridHead class="weekday-group">
                    <CalendarGridRow class="row weekdays">
                        <CalendarHeadCell v-for="(day, index) in weekDays" :key="day" class="weekday">
                            {{ weekdayLabels(weekDays)[index] }}
                        </CalendarHeadCell>
                    </CalendarGridRow>
                </CalendarGridHead>
                <CalendarGridBody class="weeks">
                    <CalendarGridRow v-for="(weekDates, index) in month.rows" :key="`weekDate-${index}`" class="row">
                        <CalendarCell v-for="weekDate in weekDates" :key="weekDate.toString()" :date="weekDate" class="cell">
                            <CalendarCellTrigger :day="weekDate" :month="month.value" class="day" />
                        </CalendarCell>
                    </CalendarGridRow>
                </CalendarGridBody>
            </CalendarGrid>
        </div>
    </CalendarRoot>
</template>
<script setup lang="ts">
/* eslint design-system/no-off-scale-spacing: "off" -- The 2px cell gaps define the date-picker spec's 280px fixed-width grid (7 x 36px cells + 6 x 2px gaps + 16px padding). */
import { DateFormatter, type DateValue, toCalendarDate } from "@internationalized/date";
import {
    CalendarCell,
    CalendarCellTrigger,
    CalendarGrid,
    CalendarGridBody,
    CalendarGridHead,
    CalendarGridRow,
    CalendarHeadCell,
    CalendarHeader,
    CalendarHeading,
    CalendarNext,
    CalendarPrev,
    CalendarRoot,
    type CalendarRootProps,
} from "reka-ui";
import { computed } from "vue";

type CalendarSurface = "raised" | "outlined" | "plain";

interface BaseCalendarProps extends CalendarRootProps {
    /** raised floats on a shadow, outlined sits inline with a 1px border, plain has neither (for use inside a popover or card). */
    surface?: CalendarSurface;
}

const { surface = "raised", weekStartsOn = 1, weekdayFormat = "short", fixedWeeks = true, ...calendarProps } = defineProps<BaseCalendarProps>();

const rootProps = computed(() => ({ ...calendarProps, weekStartsOn, weekdayFormat, fixedWeeks }));

const surfaceClasses: Record<CalendarSurface, string> = {
    raised: "is-raised",
    outlined: "is-outlined",
    plain: "",
};

const surfaceClass = computed(() => surfaceClasses[surface]);

function headingParts(date: DateValue, locale: string): Intl.DateTimeFormatPart[] {
    return new DateFormatter(locale, { month: "long", year: "numeric", timeZone: "UTC" }).formatToParts(toCalendarDate(date).toDate("UTC"));
}

/** Short labels are trimmed to two letters ("Mo") unless that would make two columns identical (e.g. pt-BR "qua."/"qui."). */
function weekdayLabels(weekDays: string[]): string[] {
    if (weekdayFormat !== "short") {
        return weekDays;
    }
    const compact = weekDays.map((day) => day.slice(0, 2));
    return new Set(compact).size === weekDays.length ? compact : weekDays;
}
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    .calendar {
        @apply w-fit max-w-full select-none rounded-lgr bg-calendar-bg p-2 font-scaffold text-calendar-day-text;
    }

    .calendar.is-raised {
        @apply shadow-calendar;
    }

    .calendar.is-outlined {
        @apply border border-calendar-border;
    }

    .head {
        @apply flex items-center justify-between px-1 pt-1 pb-2;
    }

    .heading {
        @apply m-0 font-scaffold text-[14px] leading-5 font-semibold tracking-[-0.01em] [word-spacing:1.5px];
    }

    .year {
        @apply font-normal text-ink-muted;
    }

    .nav {
        @apply flex gap-1;
    }

    .nav-button {
        @apply inline-flex size-7 cursor-pointer items-center justify-center rounded-lg border border-transparent text-calendar-day-text transition-colors hover:bg-calendar-nav-hover-bg active:bg-calendar-nav-active-bg focus-visible:border-calendar-focus-border focus-visible:ring-3 focus-visible:ring-calendar-focus-halo focus-visible:outline-none data-disabled:cursor-not-allowed data-disabled:bg-transparent data-disabled:text-calendar-day-muted-text;
    }

    .nav-chevron {
        @apply size-3.5;
    }

    .months {
        @apply flex flex-col gap-4 sm:flex-row;
    }

    .month {
        @apply grid grid-cols-[repeat(7,minmax(0,--spacing(9)))] gap-x-0.5;
    }

    .calendar.has-long-weekdays .month {
        @apply grid-cols-[repeat(7,minmax(0,max-content))];
    }

    .weekday-group,
    .row {
        @apply col-span-full grid grid-cols-subgrid;
    }

    .weekdays {
        @apply pb-1;
    }

    .weekday {
        @apply text-center font-scaffold text-[11px] leading-4 font-normal tracking-[0.04em] text-ink-muted uppercase;
    }

    .calendar.has-long-weekdays .weekday {
        @apply truncate;
    }

    .weeks {
        @apply col-span-full grid grid-cols-subgrid gap-y-0.5;
    }

    .cell {
        @apply relative p-0;
    }

    /* Rules are ordered so later states win: outside-month and disabled mute
       the day, today adds a ring, selected fills over all of them, and the
       focus ring sits on top. */
    .day {
        @apply relative flex aspect-square w-full cursor-pointer items-center justify-center rounded-lg border border-transparent scaffold-body-small whitespace-nowrap text-calendar-day-text transition-colors hover:bg-calendar-day-hover-bg;
    }

    .calendar.has-long-weekdays .day {
        @apply mx-auto max-w-9;
    }

    .day[data-outside-view] {
        @apply text-calendar-day-muted-text;
    }

    .day[data-today] {
        @apply border-calendar-today-ring scaffold-body-small-medium;
    }

    .day[data-disabled],
    .day[data-unavailable] {
        @apply cursor-not-allowed bg-transparent text-calendar-day-muted-text;
    }

    .day[data-selected] {
        @apply border-calendar-day-selected-bg bg-calendar-day-selected-bg scaffold-body-small-medium text-calendar-day-selected-text hover:border-calendar-day-selected-hover-bg hover:bg-calendar-day-selected-hover-bg;
    }

    .day:focus-visible {
        @apply z-10 border-calendar-focus-border ring-3 ring-calendar-focus-halo outline-none;
    }

    .day[data-selected]:focus-visible {
        @apply border-calendar-selected-focus-border ring-calendar-selected-focus-halo;
    }
}
</style>
